import React, { useState } from 'react';
import { COMPANY_INFO } from '../data/company';

export default function ContactFormSection({ title = "Har du spørgsmål eller ønsker du et tilbud?", subtitle = "Udfyld formularen, så vender vi hurtigt tilbage til dig." }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot antispam field
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    // Honeypot check on client side
    if (formData.website_url) {
      // Spam detected silently
      setSubmitSuccess(true);
      return;
    }

    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim()) {
      setSubmitError('Udfyld venligst alle påkrævede felter (navn, telefon og e-mail).');
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          description: formData.description,
          website_url: formData.website_url
        })
      });

      if (res.ok) {
        setSubmitSuccess(true);
        setFormData({ name: '', phone: '', email: '', description: '', website_url: '' });
      } else {
        const data = await res.json().catch(() => ({}));
        setSubmitError(data.message || 'Der opstod en fejl ved afsendelsen. Prøv venligst igen eller ring til os.');
      }
    } catch (err) {
      setSubmitError('Netværksfejl ved afsendelse. Kontrollér din forbindelse eller kontakt os direkte på tlf. (+45) 98 35 15 47.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-slate-900 rounded-3xl text-white shadow-xl overflow-hidden grid grid-cols-1 lg:grid-cols-12">
          
          {/* Left Info Column */}
          <div className="lg:col-span-5 p-8 sm:p-12 bg-slate-900 flex flex-col justify-between relative overflow-hidden border-b lg:border-b-0 lg:border-r border-slate-800">
            <div className="absolute top-0 right-0 -mt-12 -mr-12 w-64 h-64 bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />
            
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-bold uppercase tracking-wider mb-4 border border-blue-500/30">
                <i className="fa-solid fa-headset text-blue-400" aria-hidden="true" />
                <span>Kontakt os</span>
              </div>

              <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight leading-tight">
                {title}
              </h2>

              <p className="mt-3 text-slate-300 text-sm leading-relaxed">
                {subtitle}
              </p>

              <div className="mt-8 space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-blue-400 shrink-0 border border-slate-700">
                    <i className="fa-solid fa-location-dot" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="block text-xs font-semibold uppercase text-slate-400">Adresse</span>
                    <span className="text-white font-medium text-sm">{COMPANY_INFO.address}</span>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-blue-400 shrink-0 border border-slate-700">
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="block text-xs font-semibold uppercase text-slate-400">Telefon</span>
                    <a
                      href={`tel:${COMPANY_INFO.phoneRaw}`}
                      className="text-white hover:text-blue-400 font-bold text-sm transition-colors whitespace-nowrap-custom"
                    >
                      {COMPANY_INFO.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-sky-400 shrink-0 border border-slate-700">
                    <i className="fa-solid fa-envelope" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="block text-xs font-semibold uppercase text-slate-400">E-mail</span>
                    <a
                      href={`mailto:${COMPANY_INFO.email}`}
                      className="text-white hover:text-sky-300 font-medium text-sm transition-colors whitespace-nowrap-custom"
                    >
                      {COMPANY_INFO.email}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-6 border-t border-slate-800 flex items-center justify-between text-xs text-slate-400">
              <span>Hurtig responstid</span>
              <span className="text-blue-400 font-medium flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                Åben for henvendelser
              </span>
            </div>
          </div>

          {/* Right Form Column */}
          <div className="lg:col-span-7 p-8 sm:p-12 bg-white text-slate-900">
            {submitSuccess ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12 px-4 animate-in fade-in duration-300">
                <div className="w-16 h-16 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-3xl mb-4">
                  <i className="fa-solid fa-circle-check" aria-hidden="true" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">Tak for din henvendelse!</h3>
                <p className="mt-2 text-slate-600 text-sm max-w-md">
                  Vi har modtaget din besked og vender tilbage til dig hurtigst muligt.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitSuccess(false)}
                  className="mt-6 inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
                >
                  Send en ny besked
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                {/* ANTISPAM HONEYPOT (STRICT REQUIREMENT) */}
                <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
                  <label htmlFor="website_url">Ring mig op (Please do not fill in this field)</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                {submitError && (
                  <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-sm flex items-start gap-3">
                    <i className="fa-solid fa-triangle-exclamation text-rose-500 mt-0.5" aria-hidden="true" />
                    <span>{submitError}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Navn <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Dit fulde navn"
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 text-sm transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Telefonnummer <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="f.eks. 98351547"
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 text-sm transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    E-mailadresse <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="din.email@eksempel.dk"
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 text-sm transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    Beskrivelse af opgaven
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={4}
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Beskriv hvad du ønsker hjælp til (f.eks. privatrengøring, erhvervsrengøring, vinduespolering)..."
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 text-sm transition-all resize-y"
                  />
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-base py-3.5 px-6 rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    {submitting ? (
                      <>
                        <i className="fa-solid fa-spinner animate-spin text-lg" aria-hidden="true" />
                        <span>Sender forespørgsel...</span>
                      </>
                    ) : (
                      <>
                        <span>Send forespørgsel</span>
                        <i className="fa-solid fa-paper-plane text-sm" aria-hidden="true" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
